'use client';

import React, { useState } from 'react';
import { Button, Tooltip } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import styles from './security.module.css';

interface SensitiveDataMaskProps {
  /** The actual value to display when revealed */
  value: string | number;
  /** Character used for masking (default: '*') */
  maskChar?: string;
  /** Number of trailing characters to leave visible (default: 0) */
  visibleChars?: number;
  /** Whether data is masked by default (default: true) */
  defaultMasked?: boolean;
  /** Style to apply to the value text */
  style?: React.CSSProperties;
  /** Additional CSS class */
  className?: string;
}

/**
 * Sensitive Data Mask
 *
 * Masks sensitive financial data by default with a click-to-reveal toggle.
 * SOC 2 CC6.5 / CC6.7. Mask algorithm and default-masked behavior unchanged.
 * UI chrome: ≥44px touch target, focus-visible, tabular-nums for money.
 *
 * @example
 * <SensitiveDataMask value="$125,430.00" />
 * // Displays: ••••••••••• with eye icon to reveal
 *
 * @example
 * <SensitiveDataMask value="1234-5678-9012" visibleChars={4} />
 * // Displays: ••••-••••-9012 with eye icon to reveal
 */
export function SensitiveDataMask({
  value,
  maskChar = '\u2022', // bullet character
  visibleChars = 0,
  defaultMasked = true,
  style,
  className,
}: SensitiveDataMaskProps) {
  const [isMasked, setIsMasked] = useState(defaultMasked);

  const stringValue = String(value);

  const maskedValue =
    visibleChars > 0 && stringValue.length > visibleChars
      ? maskChar.repeat(stringValue.length - visibleChars) + stringValue.slice(-visibleChars)
      : maskChar.repeat(Math.max(stringValue.length, 6));

  return (
    <span className={`${styles.maskRoot}${className ? ` ${className}` : ''}`} style={style}>
      <span
        className={`${styles.maskValue} ${isMasked ? styles.maskValueHidden : ''}`.trim()}
        aria-label={isMasked ? 'Sensitive data hidden' : undefined}
      >
        {isMasked ? maskedValue : stringValue}
      </span>
      <Tooltip title={isMasked ? 'Show value' : 'Hide value'}>
        <Button
          type="text"
          icon={isMasked ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          onClick={() => setIsMasked(!isMasked)}
          aria-label={isMasked ? 'Reveal sensitive data' : 'Hide sensitive data'}
          aria-pressed={!isMasked}
          className={styles.revealBtn}
        />
      </Tooltip>
    </span>
  );
}

export default SensitiveDataMask;
